import { useMemo, useState } from 'react'
import { useBalance } from 'wagmi'
import { useWallet } from './hooks/useWallet'
import { useFaucetReads, useQuote } from './hooks/useFaucetReads'
import { useSipReads } from './hooks/useSipReads'
import { useDrawFlow, type DrawIntent } from './hooks/useDrawFlow'
import {
  ADDRESSES,
  CBBTC_LINK,
  CHAIN,
  CONFIG_PENDING_MESSAGE,
  ETH_READY_WEI,
  EXPLORER_URL,
  contractsConfigured,
  sipConfigured,
} from './config/faucet.config'
import { formatCount, readableError, shortenAddress } from './lib/format'
import { ConnectDialog } from './components/ConnectDialog'
import { SeedMark } from './components/SeedMark'

/** The three mutually exclusive routes. 'sip' exists only once the dormant Router is wired in. */
type Route = 'leaves' | 'btc' | 'sip'

export default function App() {
  const wallet = useWallet()
  const reads = useFaucetReads(wallet.address)
  // Inert while the Router is unconfigured: no reads fire, every field stays undefined.
  const sipReads = useSipReads(wallet.address)

  const [quantity, setQuantity] = useState(1)
  // TAKE A SIP is the default entry route when the Router is configured; without it the page
  // behaves exactly as production does today: OLD LEAVES default, no sip option anywhere.
  // `sipConfigured` is a build-time constant, so this initial state never flickers.
  const [route, setRoute] = useState<Route>(sipConfigured ? 'sip' : 'leaves')
  const [showConnect, setShowConnect] = useState(false)

  const enhanced = route === 'leaves'
  const sipping = route === 'sip'

  // Refetch fires on receipt CONFIRMATION, not on submission — see useDrawFlow.
  const flow = useDrawFlow(() => {
    reads.refetch()
    sipReads.refetch()
  })

  const basket = useQuote(quantity)

  // Native ETH, for the gas readiness line only. Read-only and advisory: it
  // gates nothing, and no transaction consults it.
  const { data: ethBalance } = useBalance({
    address: wallet.address,
    query: { enabled: Boolean(wallet.address), refetchInterval: 15_000 },
  })

  const maxPerTx = Number(reads.maxDrawsPerTx ?? 100n)
  const leavesNeeded = enhanced ? (basket.leavesEnhanced ?? 0n) : 0n

  const needsCbBTCApproval =
    basket.quote !== undefined && reads.cbBTCAllowance !== undefined && reads.cbBTCAllowance < basket.quote
  const needsLeafApproval = enhanced && reads.leavesApproved === false

  /**
   * The full terms of the draw, or null until every figure is a fresh contract
   * read. Snapshotted by the flow at signing, so the success screen can keep
   * stating them after the refetched balances have moved on.
   */
  const intent: DrawIntent | null = useMemo(() => {
    if (basket.quote === undefined) return null
    const seeds = enhanced ? basket.seedsEnhanced : basket.seedsStandard
    if (seeds === undefined) return null
    if (enhanced && basket.leavesEnhanced === undefined) return null
    return {
      quantity,
      enhanced,
      maxCbBTC: basket.quote,
      leaves: enhanced ? basket.leavesEnhanced! : 0n,
      seeds,
    }
  }, [basket, quantity, enhanced])

  const shortfall = useMemo(() => {
    if (!wallet.isConnected) return null
    if (sipping) {
      if (sipReads.sipSats !== undefined && reads.cbBTCBalance !== undefined && reads.cbBTCBalance < sipReads.sipSats) {
        return `not enough cbBTC. this needs ${formatCount(sipReads.sipSats)} sats; you hold ${formatCount(reads.cbBTCBalance)}.`
      }
      return null
    }
    if (basket.quote !== undefined && reads.cbBTCBalance !== undefined && reads.cbBTCBalance < basket.quote) {
      return `not enough cbBTC. this needs ${formatCount(basket.quote)} sats; you hold ${formatCount(reads.cbBTCBalance)}.`
    }
    if (enhanced && reads.leafBalance !== undefined && reads.leafBalance < leavesNeeded) {
      return `not enough taboshi1. this returns ${formatCount(leavesNeeded)}; you hold ${formatCount(reads.leafBalance)}.`
    }
    return null
  }, [wallet.isConnected, sipping, sipReads.sipSats, basket.quote, reads.cbBTCBalance, reads.leafBalance, enhanced, leavesNeeded])

  const canSubmit =
    contractsConfigured &&
    wallet.isConnected &&
    !wallet.isWrongNetwork &&
    !flow.busy &&
    !shortfall &&
    reads.canDraw === true &&
    intent !== null &&
    quantity >= 1 &&
    quantity <= maxPerTx

  // The Router's own state gates the sip button; the faucet's canDraw does not
  // apply — the Router settles from its own inventory.
  const sipNeedsApproval =
    sipping &&
    sipReads.sipSats !== undefined &&
    sipReads.routerAllowance !== undefined &&
    sipReads.routerAllowance < sipReads.sipSats
  const sipInventoryShort =
    sipReads.seedOut !== undefined && sipReads.inventory !== undefined && sipReads.inventory < sipReads.seedOut
  const canSip =
    sipConfigured &&
    wallet.isConnected &&
    !wallet.isWrongNetwork &&
    !flow.busy &&
    !shortfall &&
    sipReads.sipsPaused === false &&
    !sipInventoryShort &&
    sipReads.sipSats !== undefined &&
    sipReads.seedOut !== undefined &&
    sipReads.seedOut > 0n

  const sats = formatCount(basket.quote)

  const settled = flow.step === 'success' || flow.step === 'sip-success' || flow.step === 'failed'

  return (
    <div className="page">
      <h1>the open faucet</h1>
      <div className="rule">----------------</div>

      <p className="intro">
        old leaves return.
        <br />
        new seeds wake.
        <br />
        the tap runs while it runs.
        <br />
        it may disappear at any time.
        <br />
        no notice is promised sake.
      </p>

      <SeedMark />

      {!contractsConfigured && <p className="alert">{CONFIG_PENDING_MESSAGE}</p>}

      {wallet.isWrongNetwork && (
        <p className="alert">
          wrong network. this page only works on {CHAIN.name.toLowerCase()}.{' '}
          <button className="inline" onClick={wallet.switchToBase} disabled={wallet.isSwitching}>
            switch
          </button>
        </p>
      )}

      {reads.paused === true && <p className="alert">draws are paused.</p>}

      {!settled && reads.canDraw === false && reads.paused === false && reads.opened === false && (
        <p className="alert">the tap is not open yet. only the canary wallet may draw.</p>
      )}

      {/* Only the CURRENT price is shown. The forward schedule is deliberately not displayed —
          the exact sats for THIS transaction stay fully visible below before anything is signed. */}
      <p className="state">
        current depth: {formatCount(reads.currentPrice)} sats
        <br />
        the tap only deepens.
      </p>

      {flow.step === 'sip-success' && flow.completedSip ? (
        /* THE SIP'S OWN STICKY SUCCESS. The figures are the ACTUALS decoded from
           the transaction's SipTaken event; the balance line is live. */
        <>
          <h2>the water answered.</h2>
          <div className="rule2">-------------------</div>

          <p className="result">
            received
            <br />
            <span className="got">{formatCount(flow.completedSip.seed)} SEED</span>
          </p>

          <p className="result">
            offered
            <br />
            {formatCount(flow.completedSip.sats)} sats
          </p>

          <p className="result">
            your seeds
            <br />
            {formatCount(reads.seedBalance)} SEED
          </p>

          <p className="action">
            <a className="tap" href={`${EXPLORER_URL}/tx/${flow.completedSip.hash}`} target="_blank" rel="noreferrer">
              [ view transaction ]
            </a>
          </p>

          <p className="action">
            <button className="tap" onClick={flow.reset}>
              [ sip again ]
            </button>
          </p>
        </>
      ) : flow.step === 'success' && flow.completed ? (
        /* THE STICKY SUCCESS SCREEN. Rendered from the snapshot taken at
           signing — never from the refetched balances, which are now empty of
           exactly what this draw spent. Only [ draw again ] leaves it. */
        <>
          <h2>the tap answered.</h2>
          <div className="rule2">-----------------</div>

          <p className="result">
            received
            <br />
            <span className="got">{formatCount(flow.completed.seeds)} SEED</span>
          </p>

          {flow.completed.enhanced && (
            <p className="result">
              returned
              <br />
              {formatCount(flow.completed.leaves)} taboshi1
            </p>
          )}

          <p className="result">
            offered
            <br />
            {formatCount(flow.completed.maxCbBTC)} sats
          </p>

          <p className="result">
            your SEED balance
            <br />
            {formatCount(reads.seedBalance)}
          </p>

          <p className="action">
            <a className="tap" href={`${EXPLORER_URL}/tx/${flow.completed.hash}`} target="_blank" rel="noreferrer">
              [ view transaction ]
            </a>
          </p>

          <p className="action">
            <button className="tap" onClick={flow.reset}>
              [ draw again ]
            </button>
          </p>
        </>
      ) : flow.step === 'failed' ? (
        /* A REVERTED RECEIPT. The draw is all-or-nothing on chain, so nothing
           moved — and no SEED figure appears here, because none was received. */
        <>
          <h2>the tap did not answer.</h2>
          <div className="rule2">-----------------------</div>

          <p className="result">
            the transaction was reverted on chain.
            <br />
            nothing was taken. nothing was received.
          </p>

          {flow.hash && (
            <p className="action">
              <a className="tap" href={`${EXPLORER_URL}/tx/${flow.hash}`} target="_blank" rel="noreferrer">
                [ view transaction ]
              </a>
            </p>
          )}

          <p className="action">
            <button className="tap" onClick={flow.reset}>
              [ try again ]
            </button>
          </p>
        </>
      ) : (
        <>
          <h2>before you turn the tap</h2>
          <div className="rule2">-----------------------</div>

          <p className="need">
            you need cbBTC on Base.
            <br />
            you also need a little ETH on Base for gas.
          </p>

          <p className="need">
            {/* Outbound only. No swap is embedded and no router is called. */}
            <a className="getlink" href={CBBTC_LINK} target="_blank" rel="noreferrer noopener">
              [ get cbBTC ]
            </a>
          </p>

          {wallet.isConnected && (
            <p className="ready small muted">
              cbBTC: {formatCount(reads.cbBTCBalance)} sats
              <br />
              ETH: {ethBalance === undefined ? '—' : ethBalance.value >= ETH_READY_WEI ? 'ready' : 'low'}
              <br />
              taboshi1: {formatCount(reads.leafBalance)}
            </p>
          )}

          <div className="choice">
            {/* THE DORMANT ROUTE, FIRST AND DEFAULT WHEN CONFIGURED. Absent entirely until
                the Router address is wired in at the future manual launch — until then this
                page is byte-for-byte the production page (OLD LEAVES leads and is default).
                A sip is immediate: no rounds, no waiting, no claims. */}
            {sipConfigured && (
              <label className={route === 'sip' ? 'opt on' : 'opt'}>
                <input
                  type="radio"
                  name="mode"
                  checked={route === 'sip'}
                  onChange={() => setRoute('sip')}
                  disabled={flow.busy}
                />
                <span className="optbody">
                  <span className="optname">TAKE A SIP</span>
                  {/* Satoshi and SEED only, straight from previewSip(). The internal pricing
                      target is deliberately never rendered — no fiat figure appears anywhere. */}
                  <span className="optline">offer {formatCount(sipReads.sipSats)} sats</span>
                  <span className="optgap" />
                  <span className="optline">receive</span>
                  <span className="optline strong">{formatCount(sipReads.seedOut)} SEED</span>
                </span>
              </label>
            )}

            <label className={route === 'leaves' ? 'opt on' : 'opt'}>
              <input
                type="radio"
                name="mode"
                checked={route === 'leaves'}
                onChange={() => setRoute('leaves')}
                disabled={flow.busy}
              />
              <span className="optbody">
                <span className="optname">OLD LEAVES</span>
                <span className="optline">return {formatCount(basket.leavesEnhanced)} taboshi1</span>
                <span className="optline">offer {sats} sats</span>
                <span className="optgap" />
                <span className="optline">receive</span>
                <span className="optline strong">{formatCount(basket.seedsEnhanced)} SEED</span>
              </span>
            </label>

            <label className={route === 'btc' ? 'opt on' : 'opt'}>
              <input
                type="radio"
                name="mode"
                checked={route === 'btc'}
                onChange={() => setRoute('btc')}
                disabled={flow.busy}
              />
              <span className="optbody">
                <span className="optname">BTC ONLY</span>
                <span className="optline">offer {sats} sats</span>
                <span className="optgap" />
                <span className="optline">receive</span>
                <span className="optline strong">{formatCount(basket.seedsStandard)} SEED</span>
              </span>
            </label>
          </div>

          {/* One sip per transaction; the quantity input belongs to the tap routes. */}
          {!sipping && (
            <p className="qty">
              <label>
                quantity:{' '}
                <input
                  type="number"
                  min={1}
                  max={maxPerTx}
                  value={quantity}
                  disabled={flow.busy}
                  onChange={(e) => {
                    const n = Number(e.target.value)
                    setQuantity(Number.isFinite(n) ? Math.min(Math.max(Math.trunc(n), 1), maxPerTx) : 1)
                  }}
                />
              </label>{' '}
              <span className="muted">max {maxPerTx} per transaction</span>
            </p>
          )}

          {sipping && sipReads.sipsPaused === true && <p className="alert">sips are paused.</p>}

          {/* The keeper refills inventory automatically; the reads poll every 10s, so this line
              disappears and TAKE A SIP re-enables by itself once the replenish confirms. A muted
              button never sits here without its reason. */}
          {sipping && sipReads.sipsPaused !== true && sipInventoryShort && !flow.busy && (
            <p className="muted small">seed inventory is refilling.</p>
          )}

          {/* Eligibility warnings are held back while a transaction is in
              flight: a mid-confirmation refetch must not flash "not enough"
              over a draw that is about to succeed. */}
          {shortfall && !flow.busy && <p className="alert">{shortfall}</p>}
          {(flow.error || wallet.error) && <p className="alert">{flow.error ?? wallet.error}</p>}

          <p className="action">
            {!wallet.isConnected ? (
              <button className="tap" onClick={() => setShowConnect(true)}>
                [ CONNECT WALLET ]
              </button>
            ) : sipping ? (
              sipNeedsApproval ? (
                <button className="tap" onClick={flow.approveCbBTCForRouter} disabled={flow.busy}>
                  {flow.step === 'approving-cbbtc-sip' ? '[ APPROVING... ]' : '[ APPROVE CBBTC ]'}
                </button>
              ) : (
                <button
                  className="tap"
                  onClick={() =>
                    sipReads.sipSats !== undefined &&
                    sipReads.seedOut !== undefined &&
                    flow.sip(sipReads.sipSats, sipReads.seedOut)
                  }
                  disabled={!canSip}
                >
                  {flow.step === 'sipping' || flow.step === 'confirming' ? '[ SIPPING... ]' : '[ TAKE A SIP ]'}
                </button>
              )
            ) : needsLeafApproval ? (
              <button className="tap" onClick={flow.approveLeaves} disabled={flow.busy || !contractsConfigured}>
                {flow.step === 'approving-leaves' ? '[ APPROVING... ]' : '[ APPROVE TABOSHI1 ]'}
              </button>
            ) : needsCbBTCApproval ? (
              <button className="tap" onClick={flow.approveCbBTC} disabled={flow.busy || !contractsConfigured}>
                {flow.step === 'approving-cbbtc' ? '[ APPROVING... ]' : '[ APPROVE CBBTC ]'}
              </button>
            ) : (
              <button className="tap" onClick={() => intent && flow.draw(intent)} disabled={!canSubmit}>
                {flow.step === 'drawing' || flow.step === 'confirming' ? '[ RUNNING... ]' : '[ TURN ON THE TAP ]'}
              </button>
            )}
          </p>

          {/* The exact amounts stay on screen right up to the signature, so nothing
              is signed against a figure the participant has to remember. A sip shows
              satoshi and SEED only — no fiat figure is ever rendered. */}
          <p className="commit">
            {sipping
              ? `you offer ${formatCount(sipReads.sipSats)} sats. you receive ${formatCount(sipReads.seedOut)} SEED.`
              : enhanced
                ? `you offer ${sats} sats and return ${formatCount(basket.leavesEnhanced)} taboshi1. you receive ${formatCount(basket.seedsEnhanced)} SEED.`
                : `you offer ${sats} sats. you receive ${formatCount(basket.seedsStandard)} SEED.`}
          </p>

          {flow.hash && (
            <p className="muted small">
              {flow.receiptLabel} ·{' '}
              <a href={`${EXPLORER_URL}/tx/${flow.hash}`} target="_blank" rel="noreferrer">
                transaction
              </a>
            </p>
          )}
        </>
      )}

      <p className="bottom">
        draws: {formatCount(reads.totalDraws)}
        <br />
        seed awakened: {formatCount(reads.totalSeedsMinted)}
      </p>

      <p className="bottom small muted">
        contracts
        <br />
        faucet:{' '}
        <a href={`${EXPLORER_URL}/address/${ADDRESSES.faucet}`} target="_blank" rel="noreferrer">
          {shortenAddress(ADDRESSES.faucet)}
        </a>
        <br />
        seed:{'   '}
        <a href={`${EXPLORER_URL}/address/${ADDRESSES.seeds}`} target="_blank" rel="noreferrer">
          {shortenAddress(ADDRESSES.seeds)}
        </a>
      </p>

      <p className="verse muted">
        tw seeds are sown where still ponds gleam,
        <br />
        for toads who listened beneath the stream.
        <br />
        the world is wide, its gates for all,
        <br />
        yet lore rewards those who heed the call.
        <br />
        what grows from seeds, few will know
        <br />
        until the studied lore begin to show.
      </p>

      {showConnect && (
        <ConnectDialog
          connectors={wallet.connectors}
          onClose={() => setShowConnect(false)}
          onConnect={async (c) => {
            const ok = await wallet.connect(c)
            if (ok) setShowConnect(false)
          }}
          error={wallet.error ? readableError(wallet.error) : null}
        />
      )}
    </div>
  )
}
